// MODALS (Andrei, 2026-10-27: "all these shouldn't be pages, all this should be modals that you
// open... do the thing and then you close it and you stay here"): the company-wide quick adds
// that have no page of their own to open from — ⌘K's "Invite person" and "New seat", Home's
// setup checklist — open their dialog over whatever page is showing instead of going to Setup.
// The Setup pages' own buttons and URLs (`?invite=1`, `?new=1`) still open the same dialogs
// there. One host per company shell; `useQuickAdd()` is null outside it (callers fall back to
// the page).
import { toast } from "@companyos/ui";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { metricsQuery, rolesQuery, seatsQuery } from "../queries";
import { InviteDialog } from "../settings/people";
import { MetricDialog } from "../structure/metric-dialog";
import { NewSeatDialog } from "../structure/seats-screen";
import { useEveryPage } from "../use-every-page";
import { QuickAddPreparing } from "./preparing";

export type QuickAddKind = "invite" | "seat" | "metric";

const QuickAddContext = createContext<((kind: QuickAddKind) => void) | null>(null);

/** Opens a company quick add over the current page; null outside a company shell. */
export function useQuickAdd(): ((kind: QuickAddKind) => void) | null {
  return useContext(QuickAddContext);
}

const inDialog = (el: Element | null) =>
  el instanceof HTMLElement && el.closest('[role="dialog"],[role="alertdialog"]') !== null;

export function QuickAddHost({
  api,
  company,
  children,
}: {
  api: Api;
  company: CompanyAccess;
  children: ReactNode;
}) {
  const [open, setOpen] = useState<QuickAddKind | null>(null);
  // Focus goes back on close to what opened the quick add. Opened from ⌘K, that is what had
  // focus before the palette (the palette's own input is gone by then): the host keeps the last
  // element focused outside any dialog.
  const lastOutside = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      const t = e.target;
      if (t instanceof HTMLElement && t !== document.body && !inDialog(t)) lastOutside.current = t;
    };
    document.addEventListener("focusin", onFocus);
    return () => document.removeEventListener("focusin", onFocus);
  }, []);
  const opener = useRef<HTMLElement | null>(null);
  const start = useCallback((kind: QuickAddKind) => {
    const el = document.activeElement;
    opener.current =
      el instanceof HTMLElement && el !== document.body && !inDialog(el) ? el : lastOutside.current;
    setOpen(kind);
  }, []);
  const close = () => {
    setOpen(null);
    const el = opener.current;
    opener.current = null;
    setTimeout(() => {
      if (inDialog(document.activeElement)) return;
      if (el?.isConnected) el.focus({ preventScroll: true });
    }, 0);
  };
  return (
    <QuickAddContext.Provider value={start}>
      {children}
      {open === "invite" ? (
        <InviteDialog
          api={api}
          company={company}
          open
          onOpenChange={(o) => {
            if (!o) close();
          }}
          onSent={() => {}}
        />
      ) : open === "seat" ? (
        <HostNewSeat api={api} company={company} onClose={close} />
      ) : open === "metric" ? (
        <HostNewMetric api={api} company={company} onClose={close} />
      ) : null}
    </QuickAddContext.Provider>
  );
}

function HostNewSeat({
  api,
  company,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const roles = useInfiniteQuery(rolesQuery(api, company.id));
  const seats = useInfiniteQuery(seatsQuery(api, company.id));
  const rolesAll = useEveryPage(roles);
  const seatsAll = useEveryPage(seats);
  if (!roles.data || !seats.data || !rolesAll.complete || !seatsAll.complete)
    return (
      <QuickAddPreparing
        title="New seat"
        failed={rolesAll.failed || seatsAll.failed}
        retry={() => {
          if (rolesAll.failed) rolesAll.retry();
          if (seatsAll.failed) seatsAll.retry();
        }}
        onClose={onClose}
      />
    );
  return (
    <NewSeatDialog
      api={api}
      co={company.id}
      roles={roles.data.pages.flatMap((p) => p.data)}
      seats={seats.data.pages.flatMap((p) => p.data).filter((s) => s.status !== "closed")}
      onClose={onClose}
      onCreated={() => {
        // The chart, the seat lists and Home's checklist read the new seat.
        void qc.invalidateQueries({ queryKey: ["company", company.id] });
        toast.add({ title: "Seat created", type: "success" });
        onClose();
      }}
    />
  );
}

function HostNewMetric({
  api,
  company,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  onClose: () => void;
}) {
  const metrics = useInfiniteQuery(metricsQuery(api, company.id));
  const every = useEveryPage(metrics);
  if (!metrics.data || !every.complete)
    return (
      <QuickAddPreparing
        title="New metric"
        failed={every.failed}
        retry={every.retry}
        onClose={onClose}
      />
    );
  const all = metrics.data.pages.flatMap((p) => p.data);
  return (
    <MetricDialog
      api={api}
      co={company.id}
      existingKeys={new Set(all.map((m) => m.key))}
      metrics={all}
      agency={company.roles.includes("agency_operator")}
      onClose={onClose}
      onSaved={() => toast.add({ title: "Metric saved", type: "success" })}
    />
  );
}
